import {Component, HostBinding, HostListener, Input, OnDestroy, Optional, Self, ChangeDetectionStrategy} from '@angular/core';
import {JsonEditorOptions} from 'ang-jsoneditor';
import {Subject} from 'rxjs';
import {NgControl} from '@angular/forms';
import {MatFormFieldControl} from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import {GuidInsertService} from '../../../services/guid-insert/guid-insert.service';
import { JsonEditorDialogComponent, JsonEditorDialogResult } from './json-editor-dialog.component';

@Component({
    selector: 'app-json',
    templateUrl: './json.component.html',
    styleUrls: ['./json.component.scss'],
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: JsonComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class JsonComponent implements OnDestroy {

  static nextId = 0;

  @HostBinding() id = `json-input-${JsonComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  @Input() placeholder = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() readOnly = false;
  @Input() baseTable = '';
  @Input() fieldHint = '';

  value: object | null = null;
  initialValue: object | null = null;

  stateChanges = new Subject<void>();
  focused = false;
  errorState = false;
  controlType = 'json-input';

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private guidInsertService: GuidInsertService,
    private dialog: MatDialog,
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  // Called when the value changes
  writeValue(value: any): void {
    this.applyValue(value, true);
  }

  // Registers a callback function for when the control's value changes in the UI
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Registers a callback function for when the control is touched
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Called when the component is disabled or enabled
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Update value and notify parent form
  handleChange(value: object): void {
      if (!(value instanceof Event)) {
          this.applyValue(value, false);
          this.onChange(this.value); // Notify Angular form control
          this.onTouched(); // Mark as touched
      }
  }

  openExpandedEditor(): void {
    if (this.disabled) {
      return;
    }

    this.dialog.open(JsonEditorDialogComponent, {
      width: '90vw',
      maxWidth: '1200px',
      height: '85vh',
      data: {
        value: this.cloneValue(this.value ?? this.initialValue ?? {}),
        readOnly: this.readOnly,
      },
    }).afterClosed().subscribe((result?: JsonEditorDialogResult) => {
      if (!result?.saved) {
        return;
      }

      this.applyValue(result.value, true);
      this.onChange(this.value);
      this.onTouched();
    });
  }

  // MatFormFieldControl

  get empty(): boolean {
    return !this.value;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).tagName.toLowerCase() !== 'input') {
      (event.target as HTMLElement).focus();
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  // Update focus state
  onFocus(): void {
    this.focused = true;
    this.stateChanges.next();
  }

  onBlur(): void {
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent): void {
    this.guidInsertService.setLastFocused(event.target as HTMLElement);
  }

  @HostListener('mouseup')
  onMouseUp(): void {
    this.guidInsertService.setLastSelection(window.getSelection()?.toString());
  }

  @HostListener('keyup')
  onKeyUp(): void {
    this.guidInsertService.setLastSelection(window.getSelection()?.toString());
  }

  editorOptions(): any {
    const options = new JsonEditorOptions();
    if (this.readOnly) {
      options.mode = 'view';
      options.modes = ['view', 'tree'];
      options.onEditable = () => false;
    } else {
      options.modes = ['text', 'tree', 'view'];
    }
    return options;
  }

  private applyValue(value: any, syncEditorData: boolean): void {
    const clonedValue = this.cloneValue(value);
    this.value = clonedValue;
    if (syncEditorData) {
      this.initialValue = clonedValue;
    }
    this.stateChanges.next();
  }

  private cloneValue<T>(value: T): T {
    if (value === undefined) {
      return value;
    }

    try {
      return structuredClone(value);
    } catch {
      return value === null ? value : JSON.parse(JSON.stringify(value));
    }
  }

}
